import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { colorTokens } from '@eruvaka/web-pondlogs-base';
import { usePondList } from '../hooks/usePondList';
import { usePondLocation } from '../hooks/usePondLocation';
import { deletePond, restorePond } from '../api/pondsApi';
import { PondTableView } from './PondTableView';
import { PondMapView } from './PondMapView';
import { DeletePondDialog } from './DeletePondDialogNew';
import { RestorePondDialog } from './RestorePondDialog';
import { AddPondWizard } from './AddPondWizard';
import { CultureModal } from './CultureModal';
const LIFECYCLE_TABS = ['Active', 'Inactive', 'Deleted'];
const CATEGORIES = [
    'Culture',
    'Devices',
    'Growth',
    'Feeding',
    'Water Quality',
    'Alerts',
    'Custom',
];
const CATEGORY_I18N_KEYS = {
    Culture: 'categories.culture',
    Devices: 'categories.devices',
    Growth: 'categories.growth',
    Feeding: 'categories.feeding',
    'Water Quality': 'categories.waterQuality',
    Alerts: 'categories.alerts',
    Custom: 'categories.custom',
};
const STATUS_MAP = {
    Active: 'active',
    Inactive: 'inactive',
    Deleted: 'deleted',
};
/* ── Icon helpers (inline SVG, no external deps) ── */
const IconTable = () => (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", children: [_jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "2" }), _jsx("line", { x1: "3", y1: "9", x2: "21", y2: "9" }), _jsx("line", { x1: "3", y1: "15", x2: "21", y2: "15" }), _jsx("line", { x1: "9", y1: "9", x2: "9", y2: "21" })] }));
const IconMap = () => (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", children: [_jsx("polygon", { points: "3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" }), _jsx("line", { x1: "9", y1: "3", x2: "9", y2: "18" }), _jsx("line", { x1: "15", y1: "6", x2: "15", y2: "21" })] }));
export const PondListView = memo((props) => {
    const { emptyMapContent } = props;
    const { t } = useTranslation('ponds');
    const [lifecycle, setLifecycle] = useState('Active');
    const [category, setCategory] = useState('Culture');
    const [viewMode, setViewMode] = useState('map');
    const [showZeroLocationSetup] = useState(true);
    const { ponds, page, limit, total, totalPages, isLoading, error, refresh, setStatus, setPage, setLimit, statusTotals, } = usePondList({ skipListFetch: viewMode === 'map' });
    const { locationId: activeLocationId } = usePondLocation();
    const isZeroLocationSetup = !activeLocationId && Boolean(emptyMapContent) && showZeroLocationSetup;
    // Note: the inner landing was removed — Start Setup navigates to /ponds and the map shows directly.
    // emptyMapContent is kept for the isZeroLocationSetup check (hides the control row when no location).
    /* Modal state */
    const [addWizardOpen, setAddWizardOpen] = useState(false);
    const [selectedPond, setSelectedPond] = useState(null);
    const [activateModalOpen, setActivateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    /* Sync API status with lifecycle tab */
    const handleLifecycleChange = (tab) => {
        setLifecycle(tab);
        setStatus(STATUS_MAP[tab]);
    };
    /* Counts per lifecycle from statusTotals (server-side, accurate) */
    const counts = {
        Active: statusTotals.active,
        Inactive: statusTotals.inactive,
        Deleted: statusTotals.deleted,
    };
    /* ── Handlers ── */
    const handleEdit = (pond) => {
        setSelectedPond(pond);
        setEditModalOpen(true);
    };
    const handleActivate = (pond) => {
        setSelectedPond(pond);
        setActivateModalOpen(true);
    };
    const handleDeleteRequest = (pond) => {
        setSelectedPond(pond);
        setDeleteDialogOpen(true);
    };
    const handleRestoreRequest = (pond) => {
        setSelectedPond(pond);
        setRestoreDialogOpen(true);
    };
    const handlePondSaved = async () => {
        await refresh();
    };
    const handleDeleteConfirm = async (_pondId, confirmationText) => {
        if (!selectedPond)
            return;
        await deletePond(selectedPond.id, confirmationText);
        await refresh();
        setDeleteDialogOpen(false);
        setSelectedPond(null);
    };
    const handleRestoreConfirm = async (_pondId) => {
        if (!selectedPond)
            return;
        await restorePond(selectedPond.id);
        await refresh();
        setRestoreDialogOpen(false);
        setSelectedPond(null);
    };
    return (_jsxs(Box, { sx: {
            bgcolor: colorTokens.gray50,
            minHeight: '100%',
            fontFamily: "'Nunito', 'Roboto', sans-serif",
        }, children: [_jsxs(Box, { sx: { width: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }, children: [!isZeroLocationSetup && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }, children: [_jsx(Box, { sx: {
                                    display: 'flex',
                                    p: '3px',
                                    gap: '2px',
                                    borderRadius: '12px',
                                    bgcolor: colorTokens.segmentBg,
                                    flexShrink: 0,
                                }, children: LIFECYCLE_TABS.map((tab) => {
                                    const isActive = lifecycle === tab;
                                    return (_jsxs(Box, { component: "button", onClick: () => handleLifecycleChange(tab), sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            px: 1.5,
                                            py: '5px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            fontFamily: 'inherit',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            bgcolor: isActive ? 'white' : 'transparent',
                                            color: isActive ? colorTokens.textDark : colorTokens.gray600,
                                            boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                                        }, children: [t(`status.${tab.toLowerCase()}`), _jsx(Box, { component: "span", sx: { fontSize: 10, fontWeight: 700, opacity: isActive ? 0.7 : 0.45 }, children: counts[tab] })] }, tab));
                                }) }), _jsx(Box, { sx: { width: '1px', height: 20, bgcolor: colorTokens.gray200, flexShrink: 0 } }), lifecycle === 'Active' && (_jsx(Box, { sx: {
                                    display: 'flex',
                                    p: '3px',
                                    gap: '2px',
                                    borderRadius: '12px',
                                    bgcolor: colorTokens.segmentBg,
                                    flexShrink: 0,
                                }, children: CATEGORIES.map((cat) => {
                                    const isSelected = category === cat;
                                    return (_jsx(Box, { component: "button", onClick: () => setCategory(cat), sx: {
                                            px: 1.25,
                                            py: '5px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            fontFamily: 'inherit',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            bgcolor: isSelected ? 'white' : 'transparent',
                                            color: isSelected ? colorTokens.textDark : colorTokens.gray600,
                                            boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                                        }, children: t(CATEGORY_I18N_KEYS[cat]) }, cat));
                                }) })), _jsx(Box, { sx: { flex: 1, minWidth: 8 } }), _jsx(Box, { sx: {
                                    display: 'flex',
                                    gap: '2px',
                                    p: '2px',
                                    borderRadius: '10px',
                                    bgcolor: colorTokens.gray50,
                                    border: `1px solid ${colorTokens.gray200}`,
                                    flexShrink: 0,
                                }, children: [
                                    ['table', IconTable, t('viewToggle.table')],
                                    ['map', IconMap, t('viewToggle.map')],
                                ].map(([v, Icon, tooltip]) => (_jsx(Box, { component: "button", title: tooltip, onClick: () => setViewMode(v), style: { cursor: 'pointer' }, sx: {
                                        p: 1,
                                        borderRadius: '8px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        bgcolor: viewMode === v ? 'white' : 'transparent',
                                        color: viewMode === v ? colorTokens.primary : colorTokens.gray600,
                                        boxShadow: viewMode === v ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                    }, children: _jsx(Icon, {}) }, v))) })] })), isLoading && viewMode !== 'map' && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 6 }, children: _jsx(CircularProgress, { size: 28, sx: { color: colorTokens.secondary } }) })), error && !isLoading && (_jsx(Box, { sx: {
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: `${colorTokens.error}08`,
                            border: `1px solid ${colorTokens.error}20`,
                        }, children: _jsx(Typography, { sx: { fontSize: 13, color: colorTokens.error }, children: t(error) }) })), !isLoading && viewMode === 'table' && lifecycle === 'Deleted' && counts.Deleted === 0 && (_jsx(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: `1px solid ${colorTokens.gray200}`,
                        }, children: _jsx(Typography, { sx: { fontSize: 12, mt: 1, fontWeight: 700, color: colorTokens.gray600 }, children: "No deleted ponds" }) })), !isLoading &&
                        !error &&
                        (lifecycle !== 'Deleted' || counts.Deleted > 0) &&
                        viewMode === 'table' && (_jsx(PondTableView, { ponds: ponds, category: category === 'Custom' ? 'Culture' : category, lifecycle: lifecycle, page: page, perPage: limit, totalItems: total, totalPages: totalPages, onPageChange: setPage, onPerPageChange: setLimit, onEdit: lifecycle === 'Active' ? handleEdit : undefined, onDelete: lifecycle !== 'Deleted' ? handleDeleteRequest : undefined, onActivate: lifecycle === 'Inactive' ? handleActivate : undefined, onRestore: lifecycle === 'Deleted' ? handleRestoreRequest : undefined, limit: limit, total: total, onLimitChange: setLimit })), viewMode === 'map' && (_jsx(Box, { sx: {
                            height: 560,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            border: `1px solid ${colorTokens.gray200}`,
                        }, children: _jsx(PondMapView, { locationId: activeLocationId ?? undefined, onPondSaved: () => void refresh() }) }))] }), _jsx(AddPondWizard, { open: addWizardOpen, onClose: () => setAddWizardOpen(false), onSaved: handlePondSaved }), selectedPond && activateModalOpen && (_jsx(CultureModal, { open: activateModalOpen, mode: "activate", pond: selectedPond, onClose: () => {
                    setActivateModalOpen(false);
                    setSelectedPond(null);
                }, onSaved: handlePondSaved })), selectedPond && editModalOpen && (_jsx(CultureModal, { open: editModalOpen, mode: "edit", pond: selectedPond, onClose: () => {
                    setEditModalOpen(false);
                    setSelectedPond(null);
                }, onSaved: handlePondSaved })), selectedPond && (_jsx(DeletePondDialog, { open: deleteDialogOpen, pond: selectedPond, onClose: () => {
                    setDeleteDialogOpen(false);
                    setSelectedPond(null);
                }, onConfirm: handleDeleteConfirm })), selectedPond && (_jsx(RestorePondDialog, { open: restoreDialogOpen, pond: selectedPond, onClose: () => {
                    setRestoreDialogOpen(false);
                    setSelectedPond(null);
                }, onConfirm: handleRestoreConfirm }))] }));
});
